/*
 * Helpers for loading/merging the published Stay or Go? config from the
 * admin API. The .NET `getPublishedConfig` endpoint returns a config blob
 * (or 404 if no admin override has been published yet). When 404, we fall
 * back to the prototype-derived defaults baked into calc.ts.
 *
 * Merge is deep for objects, replace-wholesale for arrays — same semantics
 * as the prototype's `mergeDeep`. Arrays in the published config (questions,
 * stats, tiers, teams) replace defaults entirely; this matches the admin
 * panel's behavior of always editing the full array.
 */

import { api } from '@shared/api/client';
import {
  DEFAULT_CONFIG,
  type StayOrGoConfig,
} from './calc';

export const CONFIG_STORAGE_KEY = 'motr-stay-or-go-config-cache-v1';
export const STATE_STORAGE_KEY = 'motr-stay-or-go-state-v1';
export const SUBMISSION_ID_KEY = 'motr_sg_submission_id';

type Plain = Record<string, unknown>;

function isPlainObject(v: unknown): v is Plain {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

export function mergeDeep<T extends Plain>(target: T, source: Plain): T {
  const out: Plain = { ...target };
  for (const key of Object.keys(source)) {
    const sv = source[key];
    const tv = out[key];
    if (isPlainObject(sv) && isPlainObject(tv)) {
      out[key] = mergeDeep(tv, sv);
    } else if (sv !== undefined) {
      out[key] = sv;
    }
  }
  return out as T;
}

export function applyDefaults(partial: Partial<StayOrGoConfig> | Plain): StayOrGoConfig {
  return mergeDeep(
    JSON.parse(JSON.stringify(DEFAULT_CONFIG)) as StayOrGoConfig & Plain,
    (partial ?? {}) as Plain,
  ) as StayOrGoConfig;
}

/**
 * Fetch the published Stay or Go? config from the API. Falls back to the
 * cached copy in localStorage on network failure (so a hard-offline buyer
 * still sees a sensible tool); falls back to the bundled defaults if there
 * is no cache. 404 from the API is normal (no admin override yet).
 */
export async function loadPublishedConfig(): Promise<StayOrGoConfig> {
  try {
    const { snapshot } = await api.getPublishedConfig<Partial<StayOrGoConfig>>(
      'stay_or_go',
    );
    if (snapshot) {
      const merged = applyDefaults(snapshot.config);
      cacheConfig(merged);
      return merged;
    }
  } catch {
    /* network down — try the cache */
    const cached = readCachedConfig();
    if (cached) return cached;
  }
  return JSON.parse(JSON.stringify(DEFAULT_CONFIG)) as StayOrGoConfig;
}

function cacheConfig(cfg: StayOrGoConfig): void {
  try {
    localStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify(cfg));
  } catch {
    /* storage disabled — fine */
  }
}

function readCachedConfig(): StayOrGoConfig | null {
  try {
    const raw = localStorage.getItem(CONFIG_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StayOrGoConfig>;
    return applyDefaults(parsed as Plain);
  } catch {
    return null;
  }
}
